import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { Avatar, Checkbox, CircularProgress, Switch } from "@mui/material";
import BaseModal from "../../../../components/ui/BaseModal";
import BaseButton from "../../../../components/ui/BaseButton";
import BaseConfirmModal from "../../../../components/ui/BaseConfirmModal";
import { getCustomerUsers } from "../../../../services/api/customerUsers";
import { fetchCompanyUsers } from "../../../../services/api/company";
import { addChatParticipants, removeChatParticipants } from "../../../../services/api/message";
import { showToast } from "../../../../store/toast/toastSlice";

const ChatParticipantsModal = ({
  open,
  close,
  chatRoomId,
  existingUserIds = [],
  participants = [],
  viewerRole = null,
  customerId = null,
  currentUserId = null,
  onChanged,
}) => {
  const dispatch = useDispatch();
  const [selectedIds, setSelectedIds] = useState([]);
  const [sharePreviousMessages, setSharePreviousMessages] = useState(true);

  useEffect(() => {
    if (!open) return;
    setSelectedIds([]);
    setSharePreviousMessages(true);
  }, [open]);

  const { data, isPending } = useQuery({
    queryKey: ["customerUsersForChat", customerId],
    queryFn: () =>
      getCustomerUsers({ limit: 100, includeOwner: true, ...(customerId ? { customerId } : {}) }),
    enabled: open,
  });

  const [search, setSearch] = useState("");
  const [isAddMode, setIsAddMode] = useState(false);
  useEffect(() => {
    if (!open) return;
    setSearch("");
    setIsAddMode(false);
  }, [open]);

  const isClientViewer = viewerRole === "CLIENT";
  const { data: teamData, isPending: isTeamPending } = useQuery({
    queryKey: ["companyUsersForChat"],
    queryFn: () => fetchCompanyUsers({ limit: 100 }),
    enabled: open && isClientViewer,
  });

  const allUsers = useMemo(() => {
    const customerUsers = (data?.data?.data ?? data?.data ?? []).map((user) => ({
      ...user,
      role: user.role ?? "CUSTOMER",
    }));
    const teamUsers = (teamData?.data?.users ?? []).map((user) => ({
      ...user,
      role: user.role?.role ?? "CLIENT",
      profilePicture: user.profilePicture ?? user.digitalAsset?.[0]?.url ?? null,
      isOwner: false,
    }));
    return [...customerUsers, ...teamUsers];
  }, [data, teamData]);
  const users = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return allUsers;
    return allUsers.filter((user) =>
      `${user.firstName ?? ""} ${user.lastName ?? ""} ${user.email ?? ""} ${user.id}`
        .toLowerCase()
        .includes(term),
    );
  }, [allUsers, search]);

  const alreadyIn = useMemo(
    () => new Set(participants.length ? participants.map((user) => user.id) : existingUserIds),
    [participants, existingUserIds],
  );

  const ownerIds = useMemo(
    () => new Set(allUsers.filter((user) => user.isOwner).map((user) => user.id)),
    [allUsers],
  );

  const members = useMemo(() => {
    const roster = participants.length
      ? participants.map((user) => ({ ...user, isOwner: ownerIds.has(user.id) }))
      : allUsers.filter((user) => alreadyIn.has(user.id));

    if (viewerRole === "CUSTOMER") {
      return roster.filter((user) => !user.role || user.role === "CUSTOMER");
    }

    return [...roster].sort((a, b) => {
      const sideOf = (user) => (user.role === viewerRole ? 1 : 0);
      if (sideOf(a) !== sideOf(b)) return sideOf(a) - sideOf(b);
      if (!!a.isOwner !== !!b.isOwner) return a.isOwner ? -1 : 1;
      return `${a.firstName ?? ""} ${a.lastName ?? ""}`.localeCompare(
        `${b.firstName ?? ""} ${b.lastName ?? ""}`,
      );
    });
  }, [participants, allUsers, alreadyIn, ownerIds, viewerRole]);
  const addable = useMemo(() => {
    const candidates = users.filter((user) => !alreadyIn.has(user.id));
    // Same order as the roster: the people across the table first, your own
    // colleagues after.
    return candidates.sort((a, b) => {
      const sideOf = (user) => (user.role === viewerRole ? 1 : 0);
      if (sideOf(a) !== sideOf(b)) return sideOf(a) - sideOf(b);
      return `${a.firstName ?? ""} ${a.lastName ?? ""}`.localeCompare(
        `${b.firstName ?? ""} ${b.lastName ?? ""}`,
      );
    });
  }, [users, alreadyIn, viewerRole]);

  const selected = useMemo(() => {
    const addableIds = new Set(addable.map((user) => user.id));
    return selectedIds.filter((id) => addableIds.has(id));
  }, [selectedIds, addable]);

  const canRemove = (user) => {
    if (user.isOwner || user.id === currentUserId) return false;
    if (viewerRole === "CUSTOMER") return user.role === "CUSTOMER" || !user.role;
    return true;
  };

  const { mutateAsync: add, isPending: isAdding } = useMutation({
    mutationFn: () => addChatParticipants(chatRoomId, { userIds: selected, sharePreviousMessages }),
  });

  const [removingId, setRemovingId] = useState(null);
  const { mutateAsync: remove } = useMutation({
    mutationFn: (userId) => removeChatParticipants(chatRoomId, { userIds: [userId] }),
  });

  const [confirmRemove, setConfirmRemove] = useState(null);

  const handleRemove = async (user) => {
    setConfirmRemove(null);
    setRemovingId(user.id);
    try {
      await remove(user.id);
      dispatch(showToast({ message: "Removed from the chat", type: "success" }));
      onChanged?.();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Could not remove from the chat.",
          type: "error",
        }),
      );
    } finally {
      setRemovingId(null);
    }
  };

  const toggle = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleAdd = async () => {
    if (!selected.length) return;
    try {
      await add();
      dispatch(
        showToast({
          message: selected.length > 1 ? "People added to the chat" : "Added to the chat",
          type: "success",
        }),
      );
      onChanged?.();
      close();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Could not add to the chat.",
          type: "error",
        }),
      );
    }
  };

  return (
    <BaseModal className="-top-40" open={open} close={close} style={{ width: "560px" }}>
      <div className="pt-6 px-7 pb-3">
        <div className="flex items-center gap-2">
          {isAddMode && (
            <button
              type="button"
              onClick={() => setIsAddMode(false)}
              className="-ml-1 rounded-md px-1.5 py-1 text-gray-500 transition hover:bg-gray-100 hover:text-dark"
              title="Back to people"
            >
              <i className="fa-light fa-arrow-left text-sm" />
            </button>
          )}
          <h6 className="text-dark font-normal">
            {isAddMode ? "Add people" : `People (${members.length})`}
          </h6>
        </div>
        <div className="mt-3 border-b border-gray-200" />

        {isAddMode && (
          <div className="relative mt-4">
            <i className="fa-light fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400" />
            <input
              autoFocus
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name, email or ID"
              className="w-full rounded-md border border-gray-200 bg-[#FAFAFA] py-2 pl-9 pr-3 text-sm outline-none placeholder:text-gray-400 focus:border-primary focus:bg-white"
            />
          </div>
        )}

        <div className="mt-3 max-h-[320px] overflow-auto pr-1">
          {isPending || (isClientViewer && isTeamPending) ? (
            <div className="flex justify-center py-10">
              <CircularProgress size="22px" />
            </div>
          ) : isAddMode ? (
            addable.length === 0 ? (
              <p className="py-8 text-center text-sm font-light text-gray-500">
                {search.trim()
                  ? "No one matches that search."
                  : "Everyone from this account is already in the chat."}
              </p>
            ) : (
              addable.map((user, index) => {
                const isSelected = selectedIds.includes(user.id);
                // A label where the side changes: adding your own colleague and
                // adding the customer's are different acts.
                const side = user.role === viewerRole ? "Your team" : "Customer";
                const showSide =
                  isClientViewer &&
                  (index === 0 ||
                    (addable[index - 1].role === viewerRole) !== (user.role === viewerRole));
                return (
                  <React.Fragment key={`group-${user.id}`}>
                    {showSide && (
                      <p className="px-2 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wider text-gray-400">
                        {side}
                      </p>
                    )}
                  <UserRow
                    key={user.id}
                    user={user}
                    isSelected={isSelected}
                    onClick={() => toggle(user.id)}
                    trailing={
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={() => toggle(user.id)}
                        onClick={(event) => event.stopPropagation()}
                      />
                    }
                  />
                  </React.Fragment>
                );
              })
            )
          ) : (
            <>
              {members.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  subtitle={user.id === currentUserId ? "You" : `ID: ${user.id}`}
                  trailing={
                    user.isOwner ? (
                      <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
                        Owner
                      </span>
                    ) : !canRemove(user) ? null : (
                      <button
                        type="button"
                        title="Remove from chat"
                        disabled={removingId === user.id}
                        onClick={() => setConfirmRemove(user)}
                        className="shrink-0 rounded-md px-2 py-1 text-xs font-light text-gray-400 opacity-0 transition hover:bg-red-50 hover:text-red-600 focus:opacity-100 group-hover/row:opacity-100 disabled:opacity-50"
                      >
                        {removingId === user.id ? "Removing…" : "Remove"}
                      </button>
                    )
                  }
                />
              ))}

            </>
          )}
        </div>

        {/* Pinned under the list, not inside it: the roster scrolls, the action
            it offers stays where the eye last left it. */}
        {!isAddMode && !isPending && (
          <div className="mt-2 border-t pt-2">
            <button
              type="button"
              onClick={() => {
                setSelectedIds([]);
                setIsAddMode(true);
              }}
              className="flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left transition hover:bg-gray-50"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-50 text-primary">
                <i className="fa-light fa-user-plus text-sm" />
              </span>
              <span className="text-sm text-dark">Add people</span>
            </button>
          </div>
        )}

        {/* The history choice only matters once somebody is picked. */}
        {isAddMode && selected.length > 0 && (
          <div className="mt-3 flex items-center gap-2 border-t pt-3">
            <Switch
              size="small"
              checked={sharePreviousMessages}
              onChange={(e) => setSharePreviousMessages(e.target.checked)}
            />
            <p className="text-xs font-light text-gray-600">
              Share previous messages
              <span className="text-gray-400">
                {sharePreviousMessages
                  ? " — they see the whole chat"
                  : " — they see new messages only"}
              </span>
            </p>
          </div>
        )}
      </div>

      <div
        className="d-flex content-center bg-white py-6"
        style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
      >
        <BaseButton
          className="px-[28px] py-[5px] bg-light text-primary hover:!text-primary"
          variant="outlined"
          color="primary"
          style={{ marginRight: isAddMode ? "16px" : "0" }}
          onClick={isAddMode ? () => setIsAddMode(false) : close}
        >
          {isAddMode ? "Cancel" : "Close"}
        </BaseButton>

        {isAddMode && (
          <BaseButton
            className="py-[5px] px-[20px] min-w-32"
            color="primary"
            loaderSize="22px"
            isLoading={isAdding}
            disabled={!selected.length || isAdding}
            onClick={handleAdd}
          >
            {selected.length > 1 ? `Add ${selected.length}` : "Add"}
          </BaseButton>
        )}
      </div>
      <BaseConfirmModal
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        onConfirm={() => handleRemove(confirmRemove)}
        confirmText="Remove"
        cancelText="Cancel"
        confirmButtonColor="error"
        type="warning"
        title="Remove from chat"
        description={`${`${confirmRemove?.firstName ?? ""} ${confirmRemove?.lastName ?? ""}`.trim()} will no longer see this chat or receive its messages. Messages they already sent stay in the conversation.`}
      />
    </BaseModal>
  );
};

export default ChatParticipantsModal;

const initialsOf = (user) =>
  `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase() || "?";

// One person — the same row on the roster and in the add list, so the two
// screens read as one place. Identified by user id: it is what the team quotes
// to each other and what the rest of the admin screens show.
const UserRow = ({ user, subtitle, trailing = null, isSelected = false, onClick }) => (
  <div
    onClick={onClick}
    className={`group/row flex items-center gap-3 rounded-lg px-2 py-2 transition ${
      onClick ? "cursor-pointer" : ""
    } ${isSelected ? "bg-blue-50/70" : "hover:bg-gray-50"}`}
  >
    <Avatar src={user.profilePicture} sx={{ width: 36, height: 36, fontSize: 13 }}>
      {initialsOf(user)}
    </Avatar>
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-normal capitalize text-dark">
        {`${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()}
      </p>
      <p className="truncate text-xs font-light text-gray-500">{subtitle ?? `ID: ${user.id}`}</p>
    </div>
    {trailing}
  </div>
);
